/**
 * ============================================================================
 * VINPOP DYNAMIC SHIPPING BAR
 * ============================================================================
 * 
 * Ce script gère l'affichage dynamique des frais de livraison dans la barre.
 * 
 * LOGIQUE:
 * --------
 * 1. Client NON connecté → Message première commande (livraison gratuite à partir de 3 bouteilles)
 * 2. Client connecté SANS commande → Prix Hollande par défaut + mention "pour les Pays-Bas"
 * 3. Client connecté AVEC commande → Prix basé sur l'adresse de livraison
 * 
 * ============================================================================
 */

(function() {
  'use strict';

  // =========================================================================
  // DONNÉES INJECTÉES PAR LIQUID (voir free-shipping-bar.liquid)
  // =========================================================================
  
  const CustomerData = window.VinpopCustomerData || {
    isLoggedIn: false,
    hasOrders: false,
    country: null,
    countryCode: null
  };

  const CartData = window.VinpopCartData || {
    itemCount: 0
  };

  const Translations = window.VinpopShippingTranslations || {
    // Messages pour clients non connectés (première commande)
    firstOrderEmpty: "Add 3 bottles for free shipping on your first order! 🚚",
    firstOrderSingular: "Add 1 more bottle for free shipping on your first order! 🚚",
    firstOrderPlural: "Add {remaining} more bottles for free shipping on your first order! 🚚",
    firstOrderAchieved: "Free shipping on your first order! 🎉",
    firstBoxAchieved: "Free shipping on your first Smart Box! 🎉",
    
    // Messages pour clients connectés avec prix
    shippingPrice: "Shipping: €{price} for {bottles} bottles",
    shippingFree: "Free shipping for {bottles}+ bottles! 🎉",
    addMoreForNextRate: "Add {remaining} more for €{nextPrice}",
    addMoreForFree: "Add {remaining} more for free shipping!",

    // Messages orientés objectif livraison gratuite (10 bouteilles)
    freeGoalEmpty: "FREE shipping from {threshold} bottles",
    freeGoalSingular: "Only 1 more bottle for FREE shipping",
    freeGoalPlural: "Only {remaining} more bottles for FREE shipping",
    freeGoalAchieved: "You unlocked FREE shipping! 🎉",

    // Message zone par défaut
    netherlandsOnly: "(Netherlands only)",
    
    // Livraison rapide
    fastDelivery: "Fast delivery (48-72h) 🚚"
  };

  const Config = window.VinpopShippingConfig;

  // =========================================================================
  // FONCTIONS UTILITAIRES
  // =========================================================================

  /**
   * Détermine la zone de livraison basée sur le pays du client
   */
  function getShippingZone(countryCode, countryName) {
    if (!Config || !Config.zones) return null;

    for (const [zoneKey, zone] of Object.entries(Config.zones)) {
      if (zone.countries.includes(countryCode) || zone.countries.includes(countryName)) {
        return { key: zoneKey, ...zone };
      }
    }
    return null;
  }

  /**
   * Obtient le tarif pour un nombre de bouteilles donné
   */
  function getShippingRate(zone, bottleCount) {
    if (!zone || !zone.rates) return null;

    const matchingRates = zone.rates.filter(function(rate) {
      return bottleCount >= rate.minBottles && bottleCount <= rate.maxBottles;
    });
    return matchingRates.sort(function(first, second) {
      return first.price - second.price;
    })[0] || zone.rates[zone.rates.length - 1]; // Le palier le moins cher gagne en cas de chevauchement
  }

  /**
   * Obtient le prochain palier de tarif
   */
  function getNextShippingRate(zone, bottleCount) {
    if (!zone || !zone.rates) return null;

    const currentRate = getShippingRate(zone, bottleCount);
    if (!currentRate) return null;

    // Trouve le tarif suivant (moins cher ou gratuit)
    for (const rate of zone.rates) {
      if (rate.minBottles > currentRate.maxBottles && rate.price < currentRate.price) {
        return rate;
      }
    }
    return null;
  }

  /**
   * Formate le prix en euros
   */
  function formatPrice(price) {
    return price.toFixed(2).replace('.', ',');
  }

  // =========================================================================
  // GÉNÉRATION DES MESSAGES
  // =========================================================================

  /**
   * Message pour client NON connecté (première commande)
   */
  function getFirstOrderMessage(bottleCount) {
    const threshold = Config ? Config.firstOrderFreeShippingThreshold : 3;

    if (bottleCount === 0) {
      return Translations.firstOrderEmpty;
    }

    if (bottleCount >= threshold) {
      return Translations.firstOrderAchieved + " - " + Translations.fastDelivery;
    }

    const remaining = threshold - bottleCount;
    if (remaining === 1) {
      return Translations.firstOrderSingular;
    }
    
    return Translations.firstOrderPlural.replace('{remaining}', remaining);
  }

  /**
   * Message pour client connecté (avec ou sans commandes)
   */
  function getLoggedInMessage(bottleCount, zone, showNetherlandsNote, countryCode, countryName) {
    // Obtenir le drapeau et le nom du pays pour l'affichage
    let countryFlag = '';
    let countryDisplayName = '';
    const lang = document.documentElement.lang || 'en';
    const langCode = lang.split('-')[0]; // 'en', 'nl', 'fr', etc.
    
    if (zone.key === 'NETHERLANDS') {
      countryFlag = zone.flag || '🇳🇱';
      countryDisplayName = (zone.displayName && zone.displayName[langCode]) || zone.displayName.en || 'Netherlands';
    } else if (zone.countryFlags && zone.countryNames) {
      // Pour France/Espagne, utiliser le pays exact
      const keyToUse = countryCode || countryName;
      countryFlag = zone.countryFlags[keyToUse] || '🚚';
      const nameObj = zone.countryNames[keyToUse];
      countryDisplayName = (nameObj && nameObj[langCode]) || (nameObj && nameObj.en) || countryName || '';
    }
    
    const countryPrefix = countryFlag ? countryFlag + ' ' : '';

    // Tout est orienté vers l'objectif "livraison gratuite" pour motiver le client
    // à atteindre le palier gratuit (10 bouteilles), au lieu d'un simple tarif.
    const freeRate = (zone.rates || []).find(function(rate) { return rate.price === 0; });
    const freeThreshold = freeRate ? freeRate.minBottles : 10;

    let message = countryPrefix;

    if (bottleCount >= freeThreshold) {
      // Objectif atteint : on félicite, pas de rappel "livraison rapide" qui dilue.
      message += Translations.freeGoalAchieved;
      if (showNetherlandsNote) {
        message += " " + Translations.netherlandsOnly;
      }
      return message;
    }

    // Panier vide : on annonce le seuil. Sinon : on décompte les bouteilles restantes.
    if (bottleCount === 0) {
      message += Translations.freeGoalEmpty.replace('{threshold}', freeThreshold);
    } else {
      const remaining = freeThreshold - bottleCount;
      const template = remaining === 1 ? Translations.freeGoalSingular : Translations.freeGoalPlural;
      message += template
        .replace('{remaining}', remaining)
        .replace('{threshold}', freeThreshold);
    }

    if (showNetherlandsNote) {
      message += " " + Translations.netherlandsOnly;
    }

    return message + " - " + Translations.fastDelivery;
  }

  // =========================================================================
  // FONCTION PRINCIPALE
  // =========================================================================

  function generateShippingMessage() {
    const bottleCount = CartData.itemCount;

    // 2026-09-28 : plus de message « livraison offerte sur la premiere commande » (client non
    // connecte) ni « premiere Smart Box livree gratuitement » (1 commande). Les remises qui les
    // rendaient vrais ont ete supprimees (voir SHIPPING_PRICING.md) : Adrien confirme qu'il n'y a
    // de livraison gratuite ni pour la premiere commande, ni pour le Tasting Kit, ni pour la
    // premiere Smart Box. Tout le monde voit la vraie regle : la grille Shopify, gratuite des
    // le palier a 0 EUR de `shipping-rates-config.js`. `getFirstOrderMessage()` reste defini,
    // plus appele que si la configuration manque.

    // ------------------------------------------------------------------
    // Tous les clients, connectes ou non
    // ------------------------------------------------------------------

    // Déterminer la zone
    let zone = getShippingZone(CustomerData.countryCode, CustomerData.country);
    let showNetherlandsNote = false;

    // Si pas de zone trouvée (pas d'adresse), utiliser la zone par défaut
    if (!zone) {
      const defaultZoneKey = Config ? Config.defaultZone : 'NETHERLANDS';
      zone = Config ? { key: defaultZoneKey, ...Config.zones[defaultZoneKey] } : null;
      
      // Si pas de commandes, indiquer que c'est pour les Pays-Bas seulement
      if (!CustomerData.hasOrders) {
        showNetherlandsNote = true;
      }
    }

    if (!zone) {
      // Fallback si pas de config
      return getFirstOrderMessage(bottleCount);
    }

    return getLoggedInMessage(bottleCount, zone, showNetherlandsNote, CustomerData.countryCode, CustomerData.country);
  }

  // =========================================================================
  // MISE À JOUR DU DOM
  // =========================================================================

  // Flag pour éviter les boucles infinies avec MutationObserver
  let isUpdating = false;

  function updateShippingBar() {
    isUpdating = true;
    
    const message = generateShippingMessage();
    const bottleCount = CartData.itemCount;
    
    // Mettre à jour TOUS les éléments avec data-content (il peut y en avoir plusieurs)
    const contentElements = document.querySelectorAll('[data-content]');
    
    contentElements.forEach(function(element) {
      element.innerHTML = message;
    });
    
    // Mettre à jour la barre de progression
    updateProgressBar(bottleCount);
    
    // Debug log (à supprimer en production)
    console.log('[VinPOP Shipping] Customer:', window.VinpopCustomerData);
    console.log('[VinPOP Shipping] Message:', message);
    
    // Réactiver après un court délai
    setTimeout(function() { isUpdating = false; }, 200);
  }

  // =========================================================================
  // INITIALISATION
  // =========================================================================

  // Variable pour stocker le nombre actuel de bouteilles
  let currentBottleCount = CartData.itemCount;

  // Fonction pour récupérer le panier actuel via l'API Shopify
  function fetchCartAndUpdate() {
    if (isUpdating) return;
    
    fetch('/cart.js')
      .then(function(response) { return response.json(); })
      .then(function(cart) {
        currentBottleCount = cart.item_count;
        updateShippingBarWithCount(currentBottleCount);
      })
      .catch(function(error) {
        console.error('[VinPOP Shipping] Error fetching cart:', error);
      });
  }

  // Fonction de mise à jour avec un count spécifique
  function updateShippingBarWithCount(bottleCount) {
    isUpdating = true;
    
    // Temporairement mettre à jour CartData
    const originalCount = CartData.itemCount;
    CartData.itemCount = bottleCount;
    
    const message = generateShippingMessage();
    
    // Restaurer
    CartData.itemCount = originalCount;
    
    // Mettre à jour TOUS les éléments avec data-content
    const contentElements = document.querySelectorAll('[data-content]');
    
    contentElements.forEach(function(element) {
      element.innerHTML = message;
    });
    
    // Mettre à jour la barre de progression
    updateProgressBar(bottleCount);
    
    console.log('[VinPOP Shipping] Updated with', bottleCount, 'bottles:', message);
    
    // Réactiver après un court délai
    setTimeout(function() { isUpdating = false; }, 200);
  }
  
  // Fonction pour mettre à jour la barre de progression
  function updateProgressBar(bottleCount) {
    const dataIsland = document.querySelector('[x-data="FreeShippingBar"]');
    if (!dataIsland) return;
    
    // Seuil de livraison gratuite : le palier a 0 EUR de la grille (12 bouteilles aux Pays-Bas
    // au 2026-09-28), le meme pour tous les clients. Plus de seuil « premiere commande » a 3.
    const defaultZone = Config && Config.zones ? Config.zones[Config.defaultZone || 'NETHERLANDS'] : null;
    const freeRate = defaultZone && Array.isArray(defaultZone.rates)
      ? defaultZone.rates.find(function(rate) { return rate.price === 0; })
      : null;
    let freeShippingThreshold = freeRate ? freeRate.minBottles : 12;
    
    // Calculer le pourcentage
    let percentage = Math.min((bottleCount / freeShippingThreshold) * 100, 100);
    
    // Mettre à jour la variable CSS
    dataIsland.style.setProperty('--progress', percentage + '%');
    
    console.log('[VinPOP Shipping] Progress:', percentage + '% (threshold:', freeShippingThreshold, ')');
  }

  // Observer les mutations du DOM pour détecter quand Alpine.js change le contenu
  function setupMutationObserver() {
    const targetNode = document.querySelector('[x-data="FreeShippingBar"]');
    if (!targetNode) {
      console.log('[VinPOP Shipping] FreeShippingBar not found, retrying...');
      setTimeout(setupMutationObserver, 500);
      return;
    }

    const observer = new MutationObserver(function(mutations) {
      if (isUpdating) return;
      
      // Si le contenu a changé, récupérer le nouveau panier et mettre à jour
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Délai pour laisser Alpine terminer sa mise à jour
          setTimeout(fetchCartAndUpdate, 100);
          break;
        }
      }
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true,
      characterData: true
    });

    console.log('[VinPOP Shipping] MutationObserver set up');
  }

  // Mettre à jour au chargement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      updateShippingBar();
      setupMutationObserver();
    });
  } else {
    updateShippingBar();
    setupMutationObserver();
  }

  // Écouter les événements de mise à jour du panier (backup)
  document.addEventListener('cart:updated', fetchCartAndUpdate);
  document.addEventListener('cart:change', fetchCartAndUpdate);

  // Exposer pour debugging
  window.VinpopShippingBar = {
    update: updateShippingBar,
    fetchAndUpdate: fetchCartAndUpdate,
    getMessage: generateShippingMessage,
    getZone: getShippingZone,
    getRate: getShippingRate
  };

})();
